import cx from 'classnames';
import {
    count_ANSWERED_QUESTIONS,
    FORUM_READ_MORE,
    FORUM_TOPIC_SET_DESCRIPTION_AND_RULES,
    num_POSTS_AND_ANSWERS_IN_LAST_WEEK,
    SUBSCRIBE_TOPIC,
    SUBSCRIBED_COUNSELLING,
    SUBSCRIBED_TOPIC,
    TOPIC_COUNSELLOR,
} from 'mk/autogenerated/translations/TopicHeader.a1643b92b4955594c69e96c59dd59d2e'
import { boldize, linebreaksbr } from 'mk/bazaar/common/filters';
import { isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import { ACTIVE_SERVER_ID } from 'mk/settings';
import { TopicCoverForm, TopicCoverFormData } from 'mk2/apps/forum/components/TopicCoverForm';
import { CONTESTS_TOPIC_ID, COUNSELLING_CATEGORY_ID, TESTINGS_TOPIC_ID } from 'mk2/apps/forum/constants';
import { TOPIC_SUBSCRIBE_TRIGGER, TOPIC_UNSUBSCRIBE_TRIGGER } from 'mk2/apps/forum/containers/Topic/Topic.actions';
import { TopicViewType } from 'mk2/apps/forum/containers/Topic/TopicPage';
import { CounsellingEntity, TopicEntity } from 'mk2/apps/forum/schemas';
import { forumTopicEditUrl, forumTopicInfoUrl } from 'mk2/apps/forum/urls';
import { linkize } from 'mk2/apps/forum/utils';
import AvatarImg from 'mk2/components/AvatarImg';
import Btn, { BtnCountType, BtnSidePadding, BtnType } from 'mk2/components/Btn';
import Cover from 'mk2/components/Cover';
import PhotosFieldNew from 'mk2/components/forms/PhotosFieldNew';
import Interpolate from 'mk2/components/Interpolate';
import LoadingPlaceholder from 'mk2/components/LoadingPlaceholder';
import { FormPageType } from 'mk2/constants';
import ActionButton from 'mk2/containers/ActionButton/ActionButton';
import { PhotosUploadConfig } from 'mk2/containers/PhotosUpload/PhotosUpload';
import { url } from 'mk2/helpers/urls';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { UserEntity } from 'mk2/schemas';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';
import styles from './TopicHeader.mscss';

export const TOPIC_COVER_PHOTOS_CONFIG = {
    photoType: 'topic',
    multiple: false,
    showPhotoCode: false,
};

interface OwnProps {
    topic: TopicEntity;
    counselling: CounsellingEntity;
    counsellingPostsCount: number | null;
    topicLoadingState: LoadingState;
    isSubscribed: boolean;
    isFavorite: boolean;
    requestUser: UserEntity;
    isModerator: boolean;
    subscribers: number;
    permissions: string[];
    currentFormPage: FormPageType;
    mobile: boolean;
    viewType: TopicViewType;

    onTopicCoverToggleMenuMore();
    onTopicCoverRemove(topicId: number);
    onSubscribe(slug: string, username: string);
    onUnsubscribe(slug: string, username: string);
    onAddToFavorites(topicId: number, userId: number);
    onRemoveFromFavorites(topicId: number, userId: number);
    onUploadFiles(slug: string, config: PhotosUploadConfig, files: File[]);
    onTopicCoverSave(values: TopicCoverFormData, topicId: number);
}

interface State {
    showFullDescription: boolean;
}

export default class TopicHeader extends React.Component<OwnProps, State> {

    private photosFieldRef: PhotosFieldNew;

    constructor(props: OwnProps) {
        super(props);

        this.state = {
            showFullDescription: false,
        };
    }

    public renderPhotosField() {
        return (
            <PhotosFieldNew
                name={'photos'}
                config={TOPIC_COVER_PHOTOS_CONFIG}
                ref={this.handlePhotosFileInputFieldRef}
                onUploadFiles={this.handleUploadFiles}
            />
        );
    }

    public renderTopicCover() {
        const { counsellingPostsCount, topic, viewType } = this.props;
        const isAskAnExpert = viewType === TopicViewType.AskAnExpert;

        let subtitle;
        if (isAskAnExpert) {
            if (counsellingPostsCount > 0) {
                subtitle = (
                    <Interpolate
                        i18nKey={count_ANSWERED_QUESTIONS}
                        count={counsellingPostsCount}
                    />
                );
            }
        } else {
            if (topic.numRecentContributors > 0) {
                subtitle = (
                    <Interpolate
                        i18nKey={num_POSTS_AND_ANSWERS_IN_LAST_WEEK}
                        num={<span className="number">{topic.numRecentContributors}</span>}
                        count={topic.numRecentContributors}
                    />
                );
            }
        }

        return (
            <Cover
                photo={topic.coverPhoto}
                title={topic.name}
                subtitle={subtitle}
            />
        );
    }

    public render() {
        const {
            topic, isSubscribed, isFavorite, requestUser, topicLoadingState, subscribers, counselling, viewType,
            onTopicCoverToggleMenuMore, onTopicCoverRemove, permissions, currentFormPage, onTopicCoverSave, mobile,
        } = this.props;

        const trimmedTopicDescription = topic.description && topic.description.substr(0, mobile ? 230 : 430);
        // we retrim topic description to check if we are not cutting words- important for markdown filter showing links
        const reTrimmedTopicDescription = trimmedTopicDescription.substr(0, Math.min(trimmedTopicDescription.length, trimmedTopicDescription.lastIndexOf(' ')));
        const isModerator = isAuthenticatedAs(requestUser, topic.moderators);
        const isAskAnExpert = viewType === TopicViewType.AskAnExpert;
        const nonFavorableTopic = (
            topic.id === TESTINGS_TOPIC_ID ||
            topic.id === CONTESTS_TOPIC_ID ||
            topic.category.id === COUNSELLING_CATEGORY_ID ||
            ['Mesiac s...', 'Měsíc s...'].includes(topic.category.name) ||
            ['Súkromné diskusie', 'Soukromé diskuze'].includes(topic.category.name)
        );
        const topicLoaded = isSuccess(topicLoadingState);

        return (
            <div className={cx(styles.TopicHeader, counselling && styles['TopicHeader--separated'])}>
                <TopicCoverForm
                    topic={topic}
                    topicCover={this.renderTopicCover()}
                    photosField={this.renderPhotosField()}
                    topicLoadingState={topicLoadingState}
                    requestUser={requestUser}
                    permissions={permissions}
                    onToggleMenuMore={onTopicCoverToggleMenuMore}
                    onRemove={onTopicCoverRemove}
                    onOpenFileExplorer={this.handleOpenFileExplorer}
                    onSave={onTopicCoverSave}
                />
                {currentFormPage !== FormPageType.ChangeTopicCover && (
                    <React.Fragment>
                        {isAskAnExpert ? !!counselling && (
                            <div className={styles.TopicHeader__counsellingFooter}>
                                <div className={styles.TopicHeader__rowWrapper}>
                                    <div className={styles.TopicHeader__counsellor}>
                                        <AvatarImg
                                            size={50}
                                            user={counselling.counsellor}
                                            className={styles.TopicHeader__counsellorAvatar}
                                            counsellorAvatar={counselling.counsellorAvatar}
                                        />
                                        <div className={styles.TopicHeader__counsellorDescription}>
                                        <div className={styles.TopicHeader__counsellorName}>
                                            {counselling.counsellorName}
                                        </div>
                                        <div className={styles.TopicHeader__counsellorBadge}>
                                            {TOPIC_COUNSELLOR}
                                        </div>
                                        </div>
                                    </div>
                                    {!isModerator && !topic.deletedAt && ACTIVE_SERVER_ID !== 207 &&
                                        <ActionButton
                                            key="subscribe"
                                            type={BtnType.BlueOutline}
                                            label={interpolate(isSubscribed ? SUBSCRIBED_COUNSELLING : SUBSCRIBE_TOPIC)}
                                            count={subscribers}
                                            countType={BtnCountType.Label}
                                            triggerAction={isSubscribed ? TOPIC_UNSUBSCRIBE_TRIGGER : TOPIC_SUBSCRIBE_TRIGGER}
                                            onClick={isSubscribed ? this.handleOnUnsubscribe : this.handleOnSubscribe}
                                            classNames={{label: styles.TopicHeader__bold}}
                                        />
                                    }
                                </div>
                                {this.state.showFullDescription ? (
                                    <div className={styles.TopicHeader__counsellingInfo}>
                                        {linebreaksbr(boldize(linkize(topic.description)))}
                                    </div>
                                ) : (
                                    <div className={styles.TopicHeader__counsellingInfo}>
                                        {linebreaksbr(boldize(linkize(reTrimmedTopicDescription)))}
                                        &nbsp;
                                        {topic.description.length > 230 &&
                                            <Btn
                                                key="show-more-btn"
                                                onClick={this.handleOnToggleShowDetails}
                                                label={FORUM_READ_MORE}
                                                sidePadding={BtnSidePadding.None}
                                                type={BtnType.BlueWithoutHeight}
                                            />
                                        }
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={styles.TopicHeader__footer}>
                                {isModerator && !topic.description && !topic.deletedAt ? (
                                    <Btn
                                        key="set-description"
                                        type={BtnType.BlueTextLight}
                                        label={FORUM_TOPIC_SET_DESCRIPTION_AND_RULES}
                                        icon="fa-info-circle fa-lg"
                                        link={url(forumTopicEditUrl, { topicId: topic.id })}
                                    />
                                ) : [
                                    !topic.deletedAt && isSubscribed && !nonFavorableTopic && (
                                        <Btn
                                            key="favorite"
                                            type={BtnType.BlueOutline}
                                            icon={isFavorite ? 'fa-star' : 'fa-star-o'}
                                            onClick={!isFavorite ? this.handleOnAddToFavorites : this.handleOnRemoveFromFavorites}
                                            className={cx(
                                                styles.TopicHeader__favorite,
                                                styles.TopicHeader__noRightBorder,
                                                {[styles['TopicHeader__favorite--on']]: isFavorite},
                                            )}
                                            disabled={!topicLoaded}
                                        />
                                    ),
                                    !topic.deletedAt && (
                                        <ActionButton
                                            key="subscribe"
                                            type={BtnType.BlueOutline}
                                            label={interpolate(isSubscribed ?  SUBSCRIBED_TOPIC : SUBSCRIBE_TOPIC)}
                                            count={subscribers}
                                            countType={BtnCountType.Label}
                                            triggerAction={isSubscribed ? TOPIC_UNSUBSCRIBE_TRIGGER : TOPIC_SUBSCRIBE_TRIGGER}
                                            onClick={isSubscribed ? this.handleOnUnsubscribe : this.handleOnSubscribe}
                                            classNames={{label: styles.TopicHeader__bold}}
                                            className={cx(
                                                styles.TopicHeader__subscribe,
                                                !topic.deletedAt && isSubscribed && !nonFavorableTopic &&
                                                    styles.TopicHeader__noLeftBorder,
                                            )}
                                            canBeVisuallyDisabled={!isModerator}
                                            disabled={isModerator}
                                        />
                                    ),
                                    <Btn
                                        key="about"
                                        type={BtnType.BlueTextLight}
                                        icon="fa-info-circle fa-lg"
                                        link={url(forumTopicInfoUrl, { topicSlug: topic.slug })}
                                        className={styles.TopicHeader__info}
                                    />,
                                ]}
                            </div>
                        )}
                    </React.Fragment>
                )}
            </div>
        );
    }

    private handleOnSubscribe = () => {
        const { topic, requestUser, onSubscribe } = this.props;
        onSubscribe(topic.slug, requestUser.username);
    };

    private handleOnUnsubscribe = () => {
        const { topic, requestUser, onUnsubscribe } = this.props;
        onUnsubscribe(topic.slug, requestUser.username);
    };

    private handleOnAddToFavorites = () => {
        const { topic, requestUser, onAddToFavorites } = this.props;
        onAddToFavorites(topic.id, requestUser.id);
    };

    private handleOnRemoveFromFavorites = () => {
        const { topic, requestUser, onRemoveFromFavorites } = this.props;
        onRemoveFromFavorites(topic.id, requestUser.id);
    };

    private handleOpenFileExplorer = () => {
        // no photos yet, show file open dialog
        this.photosFieldRef.openFileExplorer();
    };

    private handlePhotosFileInputFieldRef = (ref: PhotosFieldNew) => {
        this.photosFieldRef = ref;
    };

    private handleUploadFiles = (config: PhotosUploadConfig, files: File[]) => {
        const { topic, onUploadFiles } = this.props;
        onUploadFiles(topic.slug, config, files);
    };

    private handleOnToggleShowDetails = () => {
        this.setState({ showFullDescription: !this.state.showFullDescription });
    };
}

interface PlaceholderOwnProps {
    isLast?: boolean;
}

export const TopicHeaderPlaceholder: React.StatelessComponent<PlaceholderOwnProps> = ({ isLast }) => (
    <div className={cx(styles.TopicHeader, styles.TopicHeaderPlaceholder)}>
        <div className={styles.TopicHeader__cover}>
            <div className={cx(styles.TopicHeader__cover_photo, styles.TopicHeaderPlaceholder__cover_photo)} />
            <div className={styles.TopicHeader__cover_text}>
                <LoadingPlaceholder className={styles.TopicHeader__cover_topic_name} width={180} height={20} />
                <LoadingPlaceholder className={styles.TopicHeader__cover_posts} width={130} height={14} />
            </div>
        </div>
        <div className={styles.TopicHeader__footer}>
            <div className={cx(styles.TopicHeader__footer__subscribe, styles.TopicHeaderPlaceholder__subscribe)}>
                <LoadingPlaceholder width={120} height={25} />
            </div>
            <div className={styles.TopicHeaderPlaceholder__about}>
                <LoadingPlaceholder width={65} height={25} />
            </div>
            {/*<div className={cx(styles.TopicHeader__footer__sort, styles.TopicHeaderPlaceholder__sort)}>*/}
            {/*<LoadingPlaceholder width={30} height={25}/>*/}
            {/*</div>*/}
        </div>
    </div>
);
TopicHeaderPlaceholder.displayName = 'TopicHeaderPlaceholder';
